import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, ShieldCheck, Mail } from "lucide-react";
import { APP_NAME } from "@/lib/constants";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setSent(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6" dir="rtl">
      <div className="w-full max-w-[440px] animate-in fade-in slide-in-from-bottom-4 duration-700">
        
        <div className="text-center mb-12">
          <Link to="/" className="text-2xl font-bold text-slate-900 tracking-tighter mb-8 inline-block">
            {APP_NAME}
          </Link>
          
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm">
              <ShieldCheck className="w-8 h-8 text-slate-900" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-3">שחזור סיסמה.</h1>
          <p className="text-slate-500 text-lg">אנחנו נעזור לך לחזור לחשבון שלך במהירות.</p>
        </div>

        {sent ? (
          <div className="bg-slate-50 border border-slate-100 rounded-3xl p-10 text-center animate-in zoom-in-95 duration-500">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 text-emerald-600">
              <Mail className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">המייל בדרך אליך</h3>
            <p className="text-slate-500 leading-relaxed mb-8">
              אם הכתובת <span className="font-bold text-slate-900">{email}</span> רשומה אצלנו, יישלח אליה קישור לאיפוס סיסמה תוך דקות ספורות.
            </p>
            <Button asChild variant="outline" className="w-full h-12 rounded-full border-slate-200 font-bold">
              <Link to="/login">חזרה להתחברות</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-1">כתובת אימייל</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none text-lg px-5"
                  placeholder="name@example.com"
                  dir="ltr"
                />
              </div>
              <p className="text-xs text-slate-400 px-1 leading-relaxed">
                נשלח לך הודעה לכתובת האימייל המקושרת לחשבון שלך עם הוראות לאיפוס הסיסמה.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <Button 
                type="submit" 
                className="w-full h-14 rounded-full bg-slate-900 hover:bg-black text-white font-bold text-lg shadow-xl shadow-slate-100 transition-all active:scale-[0.98]" 
                disabled={loading}
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "שלח קישור לאיפוס"}
              </Button>
              
              <Button asChild variant="ghost" className="w-full h-12 rounded-full font-bold text-slate-500">
                <Link to="/login" className="flex items-center justify-center gap-2">
                  <ArrowRight className="h-4 w-4" />
                  חזרה להתחברות
                </Link>
              </Button>
            </div>
          </form>
        )}

        <div className="mt-20 text-center text-xs text-slate-400 font-medium">
          © {new Date().getFullYear()} {APP_NAME}. Security is our priority.
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;